import React, { useState, useEffect, useMemo } from "react";
import styles from "./Modal.module.css"; 
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../services/axiosInstance";
import TiptapEditor from "../TiptapEditor";
import {
  ClipboardPen, SquareX, Upload, User,
  CircleCheckBig, CircleX, ChevronDown, Check, Trash2, Eye
} from "lucide-react";
import ModalAdicionarAnexo from "./ModalAdicionarAnexo";

// --- ALTERAÇÃO 1: Imports corretos para visualização ---
import FileViewer from "../FileViewer"; 
import DocxViewer from "../DocxViewer";
// import PdfViewer from "../PdfViewer"; // Removido pois o FileViewer geralmente gerencia isso ou usamos a lógica condicional

import TransicaoComLoading from "../TransicaoComLoading";

// --- Helpers Visuais ---
const abreviarNome = (n = "") => {
  if (!n) return "";
  const palavras = n.replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
  if (palavras.length <= 3) return palavras.map((p) => p[0]).join("").toUpperCase();
  const chaves = ["constituição", "justiça", "finanças", "orçamento", "educação", "saúde", "obras", "segurança"];
  const pegou = palavras
    .filter((p) => chaves.some((k) => p.toLowerCase().startsWith(k)))
    .map((p) => p[0]).join("").toUpperCase();
  if (pegou.length >= 2) return pegou;
  return palavras.map((p) => p[0]).slice(0, 4).join("").toUpperCase();
};

const ModalEditarPauta = ({ pautaId, onClose, onSave }) => {
  // --- Estados do Formulário ---
  const [formData, setFormData] = useState({
    titulo: "",
    numero: "",
    ano: "",
    tipoMateriaId: null,
    status: "",
    statusTipoId: null,
    minuta: "",
    texto_completo: "",
    comissaoIds: [], 
    autores: [], 
    pautaVetadaId: null,
    pautaRelacionadoId: null,
    relacaoTipo: null,
  });

  // --- Estados de Dados (Listas) ---
  const [tiposMateria, setTiposMateria] = useState([]);
  const [comissoes, setComissoes] = useState([]);
  const [tiposStatus, setTiposStatus] = useState([]);
  const [autoresDisponiveis, setAutoresDisponiveis] = useState([]);
  
  // --- Estados de Controle UI ---
  const [loading, setLoading] = useState(true);
  const [anexosPorTipo, setAnexosPorTipo] = useState({});
  const [visualizarAnexo, setVisualizarAnexo] = useState(null);
  const [anexoModalOpen, setAnexoModalOpen] = useState(false);

  // --- Dropdowns & Filtros ---
  const [tipoOpen, setTipoOpen] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState("");
  
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusFiltro, setStatusFiltro] = useState("");

  const [comissoesOpen, setComissoesOpen] = useState(false);
  const [comissaoFiltro, setComissaoFiltro] = useState("");

  const [autoresOpen, setAutoresOpen] = useState(false);
  const [autorFiltro, setAutorFiltro] = useState("");

  // --- Vínculos ---
  const [buscaRelacionada, setBuscaRelacionada] = useState("");
  const [sugestoesRelacionadas, setSugestoesRelacionadas] = useState([]);
  const [relacionadaOpen, setRelacionadaOpen] = useState(false);
  const [pautaRelacionadaAtual, setPautaRelacionadaAtual] = useState(null);

  // =========================
  // Carregamentos Iniciais
  // =========================
  useEffect(() => {
    Promise.all([
      axiosInstance.get("/usuarios/publico"),
      axiosInstance.get("/tipos-materia"),
      axiosInstance.get("/comissoes"),
      axiosInstance.get("/tipos-status")
    ]).then(([resUser, resTipo, resCom, resStatus]) => {
      setAutoresDisponiveis(resUser.data);
      setTiposMateria(resTipo.data);
      setComissoes(resCom.data);
      setTiposStatus(resStatus.data || []);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (!pautaId) return;
    setLoading(true);
    axiosInstance.get(`/pautas/${pautaId}`).then(async (res) => {
      const dados = res.data;
      
      const autoresMapeados = dados.autores?.map((a) => 
        a.usuario ? { value: a.usuario.id, label: a.usuario.alias || a.usuario.nome } 
                  : { value: null, label: a.nomeManual }
      ) ?? [];

      let comissoesIds = dados.comissoesRelacionadas?.map((c) => c.comissao.id) ?? [];
      if (dados.comissao?.id && !comissoesIds.includes(dados.comissao.id)) {
        comissoesIds.push(dados.comissao.id);
      }

      setFormData({
        titulo: dados.titulo,
        numero: dados.numero,
        ano: dados.ano,
        tipoMateriaId: dados.tipoMateriaId,
        status: dados.status,
        statusTipoId: dados.statusTipoId ?? dados.statusTipo?.id,
        minuta: dados.minuta,
        texto_completo: dados.texto_completo,
        comissaoIds: comissoesIds,
        autores: autoresMapeados,
        pautaVetadaId: dados.pautaVetada?.id ?? null,
        pautaRelacionadoId: dados.pautaRelacionado?.id ?? null,
        relacaoTipo: dados.relacaoTipo,
      });

      setPautaRelacionadaAtual(dados.pautaRelacionado || dados.pautaVetada || null);
      await atualizarAnexos();
      setLoading(false);
    }).catch((err) => {
      console.error("Erro ao carregar pauta:", err);
      setLoading(false);
    });
  }, [pautaId]);

  // =========================
  // Helpers de Lógica
  // =========================
  const atualizarAnexos = async () => {
    try {
      const res = await axiosInstance.get(`/pautas/${pautaId}`);
      const anexos = res.data.anexos || [];
      const anexoUnico = res.data.anexo || null;
      const agrupados = {};
      
      if (anexoUnico) {
         if (!agrupados["Documento Original"]) agrupados["Documento Original"] = [];
         const jaExiste = anexos.some(a => a.nomeArquivo === anexoUnico);
         if (!jaExiste) {
             agrupados["Documento Original"].push({ id: `legacy-${anexoUnico}`, nomeArquivo: anexoUnico, tipo: "Documento Original" });
         }
      }

      anexos.forEach((anexo) => {
        const tipo = anexo.tipo || "Outros";
        if (!agrupados[tipo]) agrupados[tipo] = [];
        agrupados[tipo].push(anexo);
      });
      
      setAnexosPorTipo(agrupados);
    } catch (error) { toast.error("Erro ao atualizar anexos"); }
  };

  const excluirAnexo = async (anexoId) => {
    if(!window.confirm("Excluir este anexo permanentemente?")) return;
    try { 
        if(String(anexoId).startsWith('legacy')) {
            toast.warning("Anexos antigos não podem ser excluídos por aqui, substitua o arquivo.");
            return;
        }
        await axiosInstance.delete(`/anexos-pauta/${anexoId}`); 
        toast.success("Anexo excluído!"); 
        await atualizarAnexos(); 
    } catch (err) { toast.error("Erro ao excluir anexo"); }
  };

  // --- Filtros (Memoized) ---
  const tiposFiltrados = useMemo(() => {
    const f = tipoFiltro.toLowerCase();
    return tiposMateria.filter(t => t.nome.toLowerCase().includes(f));
  }, [tiposMateria, tipoFiltro]);

  const statusFiltrados = useMemo(() => {
    const f = statusFiltro.toLowerCase();
    return tiposStatus.filter(t => t.nome.toLowerCase().includes(f));
  }, [tiposStatus, statusFiltro]);

  const comissoesFiltradas = useMemo(() => {
      const f = comissaoFiltro.toLowerCase();
      return comissoes.filter(c => c.nome.toLowerCase().includes(f) || c.sigla?.toLowerCase().includes(f));
  }, [comissoes, comissaoFiltro]);

  const autoresFiltrados = useMemo(() => {
      const f = autorFiltro.toLowerCase();
      return autoresDisponiveis.filter(u => u.alias?.toLowerCase().includes(f) || u.nome?.toLowerCase().includes(f));
  }, [autoresDisponiveis, autorFiltro]);

  // --- PREPARAÇÃO DE AUTORES PARA IMPRESSÃO ---
  const autoresParaImpressao = useMemo(() => {
    if (!formData.autores) return [];
    return formData.autores.map(item => {
        // Se tem ID (value), busca o usuário completo para pegar a assinatura
        if (item.value) {
            const fullUser = autoresDisponiveis.find(u => u.id === item.value);
            return fullUser || { nome: item.label, cargo: "Vereador" };
        }
        // Se for autor manual
        return { nome: item.label, cargo: "Autor Externo" };
    });
  }, [formData.autores, autoresDisponiveis]);

  const nomeTipoAtual = tiposMateria.find(t => t.id === formData.tipoMateriaId)?.nome || "Selecione...";

  // --- Handlers de Seleção ---
  const toggleAutor = (u) => {
    setFormData(prev => {
        const exists = prev.autores.some(a => a.value === u.id);
        return {
            ...prev,
            autores: exists 
                ? prev.autores.filter(a => a.value !== u.id)
                : [...prev.autores, { value: u.id, label: u.alias || u.nome }]
        };
    });
  };

  const toggleComissao = (id) => {
      setFormData(prev => ({
          ...prev,
          comissaoIds: prev.comissaoIds.includes(id) 
            ? prev.comissaoIds.filter(x => x !== id) 
            : [...prev.comissaoIds, id]
      }));
  };

  // --- Busca Relacionada ---
  useEffect(() => {
    if (!buscaRelacionada.trim()) { setSugestoesRelacionadas([]); return; }
    const t = setTimeout(() => {
        axiosInstance.get("/pautas/buscar", { params: { q: buscaRelacionada, limit: 10 } })
            .then(res => {
                const rows = res.data.rows || res.data || [];
                setSugestoesRelacionadas(rows.filter(p => p.id !== pautaId));
                if(rows.length > 0) setRelacionadaOpen(true);
            }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [buscaRelacionada, pautaId]);

  const selecionarRelacionada = (p) => {
    setPautaRelacionadaAtual(p);
    
    const nomeTipoLower = nomeTipoAtual.toLowerCase();
    
    // Lógica corrigida para definir o tipo de relação correto
    let novoRelacaoTipo = 'RELACIONADO'; // Padrão
    let isVeto = false;

    if (nomeTipoLower.includes('veto')) {
        novoRelacaoTipo = 'VETO';
        isVeto = true;
    } else if (nomeTipoLower.includes('emenda')) {
        novoRelacaoTipo = 'EMENDA'; 
    }

    setFormData(prev => ({
        ...prev,
        pautaRelacionadoId: isVeto ? null : p.id,
        pautaVetadaId: isVeto ? p.id : null,
        relacaoTipo: novoRelacaoTipo 
    }));

    setBuscaRelacionada("");
    setRelacionadaOpen(false);
  };

  // =========================
  // Submit
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
        titulo: formData.titulo,
        numero: formData.numero,
        ano: Number(formData.ano),
        tipoMateriaId: Number(formData.tipoMateriaId),
        status: formData.status,
        statusTipoId: Number(formData.statusTipoId),
        minuta: formData.minuta,
        texto_completo: formData.texto_completo,
        autores: formData.autores.map(a => a.value ? { usuarioId: Number(a.value) } : { nomeManual: a.label }),
        comissaoIds: formData.comissaoIds,
        comissaoId: formData.comissaoIds.length > 0 ? formData.comissaoIds[0] : null,
        pautaRelacionadoId: formData.pautaRelacionadoId ? Number(formData.pautaRelacionadoId) : null,
        pautaVetadaId: formData.pautaVetadaId ? Number(formData.pautaVetadaId) : null,
        relacaoTipo: formData.relacaoTipo
    };

    try {
        await axiosInstance.put(`/pautas/update/${pautaId}`, payload);
        toast.success("Pauta atualizada com sucesso!");
        if (onSave) onSave();
        onClose();
    } catch (error) {
        console.error(error);
        toast.error("Erro ao salvar alterações.");
    }
  };

  const dropdownFloatingStyle = {
    position: "absolute",
    width: "100%",
    minWidth: "200px",
    top: "100%",
    left: 0,
    background: "white",
    border: "1px solid #ccc",
    zIndex: 2000,
    borderRadius: 4,
    padding: 5,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    marginTop: 2
  };

  if (loading) return <div className={styles.modalOverlay}><div className={styles.modal}><TransicaoComLoading /></div></div>;

  return (
    <>
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <h2 className={styles.modalTitle} style={{ display: 'flex', alignItems: 'center', gap: 10}}>
                Editando {formData.titulo}
              </h2>
              <button className={styles.modalClose} onClick={onClose}><SquareX /></button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalContentScrollable} style={{paddingRight: '10px'}}>
            
            {/* --- Botão Anexos no Topo --- */}
            <div style={{margin: '1rem', display: 'flex', justifyContent: 'flex-end'}}>
                 <button type="button" className={styles.btnAvancar} onClick={() => setAnexoModalOpen(true)} style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                    <Upload size={16} /> Adicionar anexos
                 </button>
            </div>

            <div className={styles.gridForms}>
                
                {/* 1. Título */}
                <div>
                    <label className={styles.inputLabel}>Título</label>
                    <div className={styles.containerInputBusca}>
                        <input className={styles.inputBusca} value={formData.titulo} onChange={e => setFormData({...formData, titulo: e.target.value})} />
                    </div>
                </div>

                {/* 2. Número e Ano */}
                <div>
                    <label className={styles.inputLabel}>Número</label>
                    <div className={styles.containerInputBusca}>
                        <input className={styles.inputBusca} value={formData.numero} onChange={e => setFormData({...formData, numero: e.target.value})} />
                    </div>
                </div>
                <div>
                    <label className={styles.inputLabel}>Ano</label>
                    <div className={styles.containerInputBusca}>
                        <input className={styles.inputBusca} value={formData.ano} onChange={e => setFormData({...formData, ano: e.target.value})} />
                    </div>
                </div>

                {/* 3. Tipo (Dropdown Flutuante) */}
                <div>
                    <label className={styles.inputLabel}>Tipo</label>
                    <div className={styles.containerInputBusca} style={{ position: 'relative' }}>
                        <div className={styles.inputBusca} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={() => setTipoOpen(!tipoOpen)}>
                            <span>{nomeTipoAtual}</span>
                            <ChevronDown size={14} style={{opacity: 0.5}}/>
                        </div>
                        {tipoOpen && (
                            <div style={dropdownFloatingStyle}>
                                <div style={{ padding: 5 }}>
                                    <input className={styles.inputBusca} style={{border: "1px solid #eee", width: "100%"}} autoFocus value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} placeholder="Pesquisar..." onClick={(e) => e.stopPropagation()} />
                                </div>
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {tiposFiltrados.map((t) => (
                                        <div key={t.id} onClick={() => { setFormData({...formData, tipoMateriaId: t.id}); setTipoOpen(false); }} className={styles.dropdownItem} style={{ padding: "8px 10px", cursor: "pointer" }}>
                                            {t.nome}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                 {/* 4. Status (Dropdown Flutuante) */}
                 <div>
                    <label className={styles.inputLabel}>Status</label>
                    <div className={styles.containerInputBusca} style={{ position: 'relative' }}>
                        <div className={styles.inputBusca} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={() => setStatusOpen(!statusOpen)}>
                            <span>{formData.status || "Selecione..."}</span>
                            <ChevronDown size={14} style={{opacity: 0.5}}/>
                        </div>
                        {statusOpen && (
                            <div style={dropdownFloatingStyle}>
                                <div style={{ padding: 5 }}>
                                    <input className={styles.inputBusca} style={{border: "1px solid #eee", width: "100%"}} autoFocus value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)} placeholder="Pesquisar..." onClick={(e) => e.stopPropagation()} />
                                </div>
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {statusFiltrados.map((s) => (
                                        <div key={s.id} onClick={() => { setFormData({...formData, status: s.nome, statusTipoId: s.id}); setStatusOpen(false); }} className={styles.dropdownItem} style={{ padding: "8px 10px", cursor: "pointer"}}>
                                            {s.nome}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 5. Vínculo (Dropdown Flutuante) */}
                <div style={{gridColumn: '1 / -1'}}>
                      <label className={styles.inputLabel}>Matéria Vinculada</label>
                      <div className={styles.containerInputBusca} style={{ position: 'relative' }}>
                        <div style={{display: "flex", gap: 5, padding: "0 8px"}}>
                            {pautaRelacionadaAtual ? (
                                <div className={styles.inputBusca}style={{display: "flex", alignItems: "center", width: "auto", justifyContent: "space-between"}}>
                                    <span>Ref: {pautaRelacionadaAtual.titulo || `#${pautaRelacionadaAtual.id}`}</span>
                                    <SquareX size={16} style={{cursor: "pointer", color: "#888"}} onClick={() => {
                                        setPautaRelacionadaAtual(null);
                                        setFormData(p => ({...p, pautaRelacionadoId: null, pautaVetadaId: null}));
                                    }} />
                                </div>
                            ) : (
                                <>
                                <input value={buscaRelacionada} onChange={(e) => setBuscaRelacionada(e.target.value)} placeholder="Buscar matéria por número ou título..." style={{ outline: "none", flex: 1, background: 'transparent'}} />
                                </>
                            )}
                        </div>
                        {relacionadaOpen && sugestoesRelacionadas.length > 0 && (
                            <div style={dropdownFloatingStyle}>
                                {sugestoesRelacionadas.map(p => (
                                    <div key={p.id} onClick={() => selecionarRelacionada(p)} className={styles.dropdownItem} style={{padding: "8px", cursor: "pointer"}}>
                                        <div style={{fontWeight: "bold"}}>{p.titulo}</div>
                                        <div style={{ color: "#666"}}>{p.minuta?.substring(0, 50)}...</div>
                                    </div>
                                ))}
                            </div>
                        )}
                      </div>
                </div>

                {/* 6. Autores (Dropdown Flutuante) */}
                <div style={{gridColumn: '1 / -1'}}>
                    <label className={styles.inputLabel}>Autores ({formData.autores.length})</label>
                    <div className={styles.containerInputBusca} style={{ position: 'relative' }}>
                        <div className={styles.inputBusca} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 40}} onClick={() => setAutoresOpen(!autoresOpen)}>
                            <div style={{display: "flex", gap: 3, overflow: "hidden", flexWrap: "wrap"}}>
                                {formData.autores.length === 0 ? <span style={{color: "#888"}}>Selecione...</span> : 
                                    formData.autores.map(a => (
                                        <span key={a.value||a.label} className={styles.tagItem} >
                                            {a.label} <SquareX size={12} onClick={(e) => {e.stopPropagation(); toggleAutor({id: a.value, alias: a.label})}} style={{marginLeft: 4, cursor:'pointer'}}/>
                                        </span>
                                    ))
                                }
                            </div>
                            <ChevronDown size={14} style={{opacity: 0.5, flexShrink: 0}}/>
                        </div>
                        {autoresOpen && (
                            <div style={dropdownFloatingStyle}>
                                <input className={styles.inputBusca} style={{border: "1px solid #eee", width: "100%", margin: 5}} autoFocus value={autorFiltro} onChange={(e) => setAutorFiltro(e.target.value)} placeholder="Filtrar..." onClick={(e) => e.stopPropagation()} />
                                <div style={{maxHeight: 200, overflowY: "auto"}}>
                                    {autoresFiltrados.map(u => {
                                        const selecionado = formData.autores.some(a => a.value === u.id);
                                        return (
                                        <div key={u.id} onClick={() => toggleAutor(u)} className={styles.dropdownItem} style={{padding: "6px", display: "flex", alignItems: "center", gap: 5, cursor: "pointer", background: selecionado ? "#f0f8ff" : "transparent"}}>
                                            <User size={14} />
                                            <span>{u.alias || u.nome}</span>
                                            {selecionado && <Check size={14} style={{marginLeft: "auto", color: "green"}} />}
                                        </div>
                                    )})}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 7. Comissões (Dropdown Flutuante) */}
                <div style={{gridColumn: '1 / -1'}}>
                    <label className={styles.inputLabel}>Comissões ({formData.comissaoIds.length})</label>
                    <div className={styles.containerInputBusca} style={{ position: 'relative' }}>
                         <div className={styles.inputBusca} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 40}} onClick={() => setComissoesOpen(!comissoesOpen)}>
                            <div style={{display: "flex", gap: 3, overflow: "hidden", flexWrap: "wrap"}}>
                                {formData.comissaoIds.length === 0 ? <span style={{color: "#888"}}>Selecione...</span> : 
                                    comissoes.filter(c => formData.comissaoIds.includes(c.id)).map(c => (
                                        <span key={c.id} className={styles.tagItem}>
                                            {abreviarNome(c.nome)} <SquareX size={12} onClick={(e) => {e.stopPropagation(); toggleComissao(c.id)}} style={{marginLeft: 4, cursor:'pointer'}}/>
                                        </span>
                                    ))
                                }
                            </div>
                            <ChevronDown size={14} style={{opacity: 0.5, flexShrink: 0}}/>
                         </div>
                         {comissoesOpen && (
                            <div style={dropdownFloatingStyle}>
                                <input className={styles.inputBusca} style={{border: "1px solid #eee", width: "100%", margin: 5}} autoFocus value={comissaoFiltro} onChange={(e) => setComissaoFiltro(e.target.value)} placeholder="Filtrar..." onClick={(e) => e.stopPropagation()} />
                                <div style={{maxHeight: 200, overflowY: "auto"}}>
                                    {comissoesFiltradas.map(c => {
                                        const selecionado = formData.comissaoIds.includes(c.id);
                                        return (
                                        <div key={c.id} onClick={() => toggleComissao(c.id)} className={styles.dropdownItem} style={{padding: "6px", display: "flex", alignItems: "center", gap: 5, cursor: "pointer", background: selecionado ? "#f0f8ff" : "transparent"}}>
                                            <span>{c.nome}</span>
                                            {selecionado && <Check size={14} style={{marginLeft: "auto", color: "green"}} />}
                                        </div>
                                    )})}
                                </div>
                            </div>
                         )}
                    </div>
                </div>

                {/* 8. Texto / Minuta */}
                <div style={{width: "100%"}}>
                    <label className={styles.inputLabel}>Ementa</label>
                    <textarea className={styles.inputBusca} rows={3} value={formData.minuta || ""} onChange={(e) => setFormData({...formData, minuta: e.target.value})} style={{width: '100%', resize: 'vertical'}}/>
                </div>

                <div style={{gridColumn: '1 / -1'}}>
                    <label className={styles.inputLabel}>Texto Completo</label>
                    <div className={styles.containerInputBusca} style={{ minHeight: 400, border: '1px solid #ccc', borderRadius: 4 }}>
                         <TiptapEditor 
                           value={formData.texto_completo || ""} 
                           onChange={(html) => setFormData({...formData, texto_completo: html})} 
                           editable={true}
                           materiaId={pautaId} 
                           autores={autoresParaImpressao}
                         />
                    </div>
                </div>

            </div> {/* Fim Grid */}

            {/* Lista de Anexos Existentes (Visualização apenas) */}
            {Object.keys(anexosPorTipo).length > 0 && (
                <div style={{marginTop: '2rem', borderTop: '1px solid #eee', paddingTop: '1rem'}}>
                    <h2 style={{color: '#555'}}>Anexos Atuais</h2>
                    <div className={styles.anexosGrid}>
                        {Object.entries(anexosPorTipo).map(([tipo, lista]) => (
                            <div key={tipo} className={styles.anexoCard}>
                                <h2 style={{ textTransform: 'uppercase', color:'#888'}}>{tipo}</h2>
                                <ul className={styles.listaBlocoMaterias}>
                                    {lista.map((anexo, i) => (
                                        <li key={i} >
                                            <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth: '70%'}} title={anexo.nomeArquivo}>{anexo.descricao || anexo.nomeArquivo}</span>
                                            <div style={{display: 'flex', gap: 5}}>
                                                <button type="button" className={styles.btnSecondary} onClick={() => setVisualizarAnexo({...anexo})} title="Ver"><Eye size={14}/></button>
                                                <button type="button" className={styles.btnSecondary} onClick={() => excluirAnexo(anexo.id)} style={{color: 'red'}} title="Excluir"><Trash2 size={14}/></button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className={styles.modalActions}>
                <button className={styles.iniciarVotacao} type="submit"><CircleCheckBig size={18} /> Salvar Alterações</button>
                <button className={styles.encerrarVotacao} type="button" onClick={onClose}><CircleX size={18} /> Cancelar</button>
            </div>
        </form>
      </div>
    </div>

    {/* --- ALTERAÇÃO 2: Modal Filho de Visualização Corrigido --- */}
    {visualizarAnexo && (
        <div className={styles.modalOverlay} style={{zIndex: 9999}} onClick={() => setVisualizarAnexo(null)}>
            <div className={styles.modal} style={{width:'80%', height:'90%'}} onClick={e => e.stopPropagation()}>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom: 10}}>
                    <h2>Visualizar Anexo</h2>
                    <button className={styles.modalClose} onClick={() => setVisualizarAnexo(null)}><SquareX/></button>
                </div>
                
                {visualizarAnexo.nomeArquivo ? (
                    visualizarAnexo.nomeArquivo.toLowerCase().endsWith('.docx') ? (
                        <DocxViewer 
                            url={`${axiosInstance.defaults.baseURL}/uploads/${visualizarAnexo.nomeArquivo}`} 
                        />
                    ) : (
                        <FileViewer url={`${axiosInstance.defaults.baseURL}/uploads/${visualizarAnexo.nomeArquivo}`} />
                    )
                ) : (
                    <div>Sem visualização disponível</div>
                )}
            </div>
        </div>
    )}

    {anexoModalOpen && (
        <ModalAdicionarAnexo 
            isOpen={anexoModalOpen} 
            closeModal={() => setAnexoModalOpen(false)} 
            pautaId={pautaId} 
            comissoes={comissoes} 
            onSave={atualizarAnexos} 
        />
    )}
    </>
  );
};

export default ModalEditarPauta;